import { Component, computed, inject, signal, viewChild, OnInit } from '@angular/core';
import { Subject, concat } from 'rxjs';
import { debounceTime, filter, toArray } from 'rxjs/operators';
import { NotifyService } from '../../service/notify.service';
import { UserAdminService } from '../../service/user-admin.service';
import { UserLoginService } from '../../service/user-login.service';
import {
  Branch,
  BranchRole,
  LocationRole,
  SelectableBranchRole,
  SelectableLocationRole,
  SystemRole,
  UserLocationResponse,
  UserProfile,
} from '../../types';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { sortBy, findIndex } from 'lodash';
import { UiModule } from '../../components/ui.module';
import { AdminUserManagementCreateUserComponent } from '../admin-user-management-create-user/admin-user-management-create-user.component';
import { SweetAlert2Module } from '@sweetalert2/ngx-sweetalert2';
import { CdkVirtualScrollViewport, ScrollDispatcher, ScrollingModule } from '@angular/cdk/scrolling';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { possibleBranchRoles, possibleLocationRoles, possibleSystemRoles } from '../../constants';
import { customSearchForIdAndName } from '../../search.utils';

type SelectableBranch = Branch & {
  _selectableRoles: SelectableBranchRole[];
};

type SelectableLocation = UserLocationResponse & {
  _selectableRoles: SelectableLocationRole[];
};

@Component({
  selector: 'app-admin-user-management-users',
  templateUrl: './admin-user-management-users.component.html',
  styleUrl: './admin-user-management-users.component.scss',
  standalone: true,
  imports: [
    UiModule,
    SweetAlert2Module,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    AdminUserManagementCreateUserComponent,
    ScrollingModule,
    MatTooltipModule,
  ],
})
export class AdminUserManagementUsersComponent implements OnInit {
  private adminService = inject(UserAdminService);
  private notifications = inject(NotifyService);
  public userLogin = inject(UserLoginService);

  private scrollDispatcher = inject(ScrollDispatcher);

  public selectedUser = signal<UserProfile | undefined>(undefined);

  public userFilter = signal<string>('');

  public allBranches = signal<SelectableBranch[]>([]);
  public allLocations = signal<SelectableLocation[]>([]);
  public users = signal<UserProfile[]>([]);
  public visibleUsers = computed(() => this.users());

  public hasLoadedUsers = signal<boolean>(false);
  public hasLoadedLocations = signal<boolean>(false);
  public hasLoadedBranches = signal<boolean>(false);
  public hasLoadedPage = computed(() => this.hasLoadedUsers() && this.hasLoadedBranches() && this.hasLoadedLocations());

  public modalBranch = signal<SelectableBranch | undefined>(undefined);
  public modalBranchRoles = signal<BranchRole[]>([]);
  public canSubmitBranchModal = computed(() => this.modalBranch() && this.modalBranchRoles().length > 0);

  public modalLocation = signal<SelectableLocation | undefined>(undefined);
  public modalLocationRoles = signal<LocationRole[]>([]);
  public canSubmitLocationModal = computed(() => this.modalLocation() && this.modalLocationRoles().length > 0);

  public branchesOrLocations = signal<'branches' | 'locations'>('branches');

  public visibleUserBranches = computed(() =>
    sortBy(
      this.allBranches().filter((b) => this.selectedUser()?.roles.branch[b.id]),
      (b) => b.name.toLowerCase(),
    ),
  );

  public visibleUserLocations = computed(() =>
    sortBy(
      this.allLocations().filter((b) => this.selectedUser()?.roles.location[b.id]),
      (b) => b.name.toLowerCase(),
    ),
  );

  public readonly possibleSystemRoles = possibleSystemRoles;

  public readonly possibleBranchRoles = possibleBranchRoles;

  public readonly possibleLocationRoles = possibleLocationRoles;

  public readonly locationSearchFn = customSearchForIdAndName;

  public currentUserPage = signal<number>(1);
  public totalUsers = signal<number>(0);
  public hasMoreUsers = signal<boolean>(false);
  public isLoadingMoreUsers = signal<boolean>(false);

  public virtualScroll = viewChild<CdkVirtualScrollViewport>('userScroll');
  public searchInput = new Subject<string>();

  public amIEditingMyself = computed(() => {
    const editingUser = this.selectedUser();
    const me = this.userLogin.currentUser();
    return me.email === editingUser.email;
  });

  constructor() {
    this.searchInput
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(300))
      .subscribe((searchTerm: string) => {
        this.userFilter.set(searchTerm);
        this.loadMoreUsers(1);
      });

    this.scrollDispatcher
      .scrolled()
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(100))
      .pipe(filter(() => this.virtualScroll().getRenderedRange().end === this.virtualScroll().getDataLength()))
      .subscribe(() => {
        if (this.hasMoreUsers() && !this.isLoadingMoreUsers()) {
          this.loadMoreUsers(this.currentUserPage() + 1);
        }
      });
  }

  ngOnInit() {
    this.loadPage();
  }

  loadPage(): void {
    this.loadLocations();
    this.loadBranches();
    this.loadUsers();
  }

  public loadLocations() {
    this.hasLoadedLocations.set(false);

    this.adminService.getMyLocations().subscribe((locations) => {
      const pageLocations = locations as SelectableLocation[];

      pageLocations.forEach((location) => {
        location._selectableRoles = this.selectableRolesForLocation(location);
      });

      this.allLocations.set(pageLocations);
      this.hasLoadedLocations.set(true);
    });
  }

  public loadBranches() {
    this.hasLoadedBranches.set(false);

    this.adminService.getMyBranches().subscribe((branches) => {
      const pageBranches = branches as SelectableBranch[];

      pageBranches.forEach((branch) => {
        branch._selectableRoles = this.selectableRolesForBranch(branch);
      });

      this.allBranches.set(pageBranches);
      this.hasLoadedBranches.set(true);
    });
  }

  public loadUsers() {
    this.hasLoadedUsers.set(false);
    this.users.set([]);

    this.loadMoreUsers(1);
  }

  public loadMoreUsers(page: number) {
    this.isLoadingMoreUsers.set(true);

    this.adminService
      .getMyUsersForBranches(
        {
          page,
          items_per_page: 200,
          search: this.userFilter(),
        },
        true,
      )
      .subscribe((usersData) => {
        this.hasMoreUsers.set(usersData.has_more);
        this.totalUsers.set(usersData.total_items);
        this.currentUserPage.set(usersData.page_number);

        if (usersData.page_number === 1) {
          this.users.set(usersData.items);
        } else {
          this.users.update((users) => [...users, ...usersData.items]);
        }

        this.isLoadingMoreUsers.set(false);
        this.hasLoadedUsers.set(true);
      });
  }

  selectUser(user: UserProfile): void {
    this.selectedUser.set(user);
  }

  addUserToList(user: UserProfile): void {
    this.users.update((users) => {
      return [user, ...users];
    });

    this.userFilter.set(user.name);
    this.selectedUser.set(user);
  }

  private selectableRolesForBranch(branch: Branch) {
    return this.possibleBranchRoles.map((role) => ({
      ...role,
      disabled: !this.userLogin.canGrantBranchPermission(role.id, branch.id.toString()),
    }));
  }

  private selectableRolesForLocation(location: UserLocationResponse) {
    return this.possibleLocationRoles.map((role) => ({
      ...role,
      disabled: !this.userLogin.canGrantLocationPermission(role.id, location.branch_id.toString(), location.id.toString()),
    }));
  }

  hasSystemRole(role: SystemRole): boolean {
    return this.selectedUser().roles.system?.includes(role) ?? false;
  }

  toggleSystemRole(role: SystemRole) {
    const user = this.selectedUser();
    const shouldRevoke = this.hasSystemRole(role);
    const res = this.hasSystemRole(role)
      ? this.adminService.revokeSystemRole(user.user_id, role)
      : this.adminService.grantSystemRole(user.user_id, role);

    res.subscribe((d) => {
      this.notifications.notify(`Successfully ${shouldRevoke ? 'revoked' : 'granted'} system role "${role}" to ${user.email}`);
      this.updateAndSetUser(d);
    });
  }

  addUserBranchRole(user: UserProfile, role: SelectableBranchRole, branch: Branch): void {
    this.adminService.grantBranchRole(user.user_id, branch.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully granted permission "${role.name}" for user "${user.name || user.email}" in branch "${branch.name}"`,
      );
    });
  }

  removeUserBranchRole(user: UserProfile, role: SelectableBranchRole, branch: Branch): void {
    this.adminService.revokeBranchRole(user.user_id, branch.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully revoked permission "${role.name}" for user "${user.name || user.email}" in branch "${branch.name}"`,
      );

      if (user.roles.branch[branch.id].length === 0) {
        this.users.update((users) => users.filter((u) => u.user_id !== user.user_id));
      }
    });
  }

  addBranchToUser(): void {
    const chosenUser = structuredClone(this.selectedUser());

    chosenUser.roles.branch[this.modalBranch().id] = this.modalBranchRoles();

    concat(
      ...this.modalBranchRoles().map((role) =>
        this.adminService.grantBranchRole(chosenUser.user_id, this.modalBranch().id.toString(), role),
      ),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(
          `Successfully added user "${chosenUser.name || chosenUser.email}" to branch "${this.modalBranch().name}"`,
        );
        this.modalBranchRoles.set([]);
        this.modalBranch.set(undefined);
        this.updateAndSetUser(chosenUser);
      });
  }

  removeUserBranchPermissions(user: UserProfile, branch: Branch): void {
    concat(
      ...user.roles.branch[branch.id.toString()].map((role) =>
        this.adminService.revokeBranchRole(user.user_id, branch.id.toString(), role),
      ),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(`Successfully removed user "${user.name || user.email}" from branch "${branch.name}"`);
        const userCopy = structuredClone(this.selectedUser());
        delete userCopy.roles.branch[branch.id.toString()];
        this.updateAndSetUser(userCopy);
      });
  }

  addUserLocationRole(user: UserProfile, role: SelectableLocationRole, location: UserLocationResponse): void {
    this.adminService.grantLocationRole(user.user_id, location.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully granted permission "${role.name}" for user "${user.name || user.email}" in location "${location.name}"`,
      );
    });
  }

  removeUserLocationRole(user: UserProfile, role: SelectableLocationRole, location: UserLocationResponse): void {
    this.adminService.revokeLocationRole(user.user_id, location.id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully revoked permission "${role.name}" for user "${user.name || user.email}" in location "${location.name}"`,
      );

      if (user.roles.location[location.id].length === 0) {
        this.users.update((users) => users.filter((u) => u.user_id !== user.user_id));
      }
    });
  }

  addLocationToUser(): void {
    const chosenUser = structuredClone(this.selectedUser());

    chosenUser.roles.location[this.modalLocation().id] = this.modalLocationRoles();

    concat(
      ...this.modalLocationRoles().map((role) =>
        this.adminService.grantLocationRole(chosenUser.user_id, this.modalLocation().id.toString(), role),
      ),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(
          `Successfully added user "${chosenUser.name || chosenUser.email}" to location "${this.modalLocation().name}"`,
        );
        this.modalLocationRoles.set([]);
        this.modalLocation.set(undefined);
        this.updateAndSetUser(chosenUser);
      });
  }

  removeUserLocationPermissions(user: UserProfile, location: UserLocationResponse): void {
    concat(
      ...user.roles.location[location.id.toString()].map((role) =>
        this.adminService.revokeLocationRole(user.user_id, location.id.toString(), role),
      ),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(`Successfully removed user "${user.name || user.email}" from location "${location.name}"`);
        const userCopy = structuredClone(this.selectedUser());
        delete userCopy.roles.location[location.id.toString()];
        this.updateAndSetUser(userCopy);
      });
  }

  public uploadPermissions($event: any) {
    const selectedFile = $event.target.files[0];
    const fileReader = new FileReader();
    fileReader.readAsText(selectedFile, 'UTF-8');
    fileReader.onload = () => {
      const newPermissions = JSON.parse(fileReader.result as string);
      this.adminService.updatePermissions(this.selectedUser().user_id, newPermissions).subscribe((d) => {
        this.notifications.notify(`Successfully imported permissions.`);
        this.updateAndSetUser(d);
      });
      $event.target.value = '';
    };
    fileReader.onerror = (error) => {
      this.notifications.notify(`Invalid permissions file: ${error}`);
      throw error;
    };
  }

  public downloadPermissions() {
    const permissions = this.selectedUser().roles;

    const text = JSON.stringify(permissions);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: `application/json` }));
    a.download = `${this.selectedUser().name ?? 'null'}-${Date.now()}.panoptycpermissions.json`;
    a.click();

    this.notifications.notify(`Downloaded permissions for ${this.selectedUser().name ?? 'user'}!`);
  }

  private updateAndSetUser(user: UserProfile) {
    this.users.update((users) => {
      const updateUser = findIndex(users, (u) => u.user_id === user.user_id);
      users.splice(updateUser, 1, user);
      return users.slice();
    });

    this.selectUser(user);
  }
}
